import React from "react";
import { COMPANY_NAME } from "@/lib/brand";
import { effectiveFieldSize } from "@/lib/labelFormat";

/**
 * Shared label renderer used by every label-print flow so a format saved on
 * the Custom Labels page is honoured everywhere.
 *
 * Per-field font sizes (fieldSizes) take priority over the global fontScale
 * multiplier — this lets operators bump only the Gross Weight to a huge 40px
 * while keeping SKU compact.
 */
export default function LabelTile({ d, preset, fontFamily, fontScale, fieldSet, customText, fieldSizes = {}, dcNetWeight = "" }) {
  const spec = (d && d.spec) || {};
  const specBits = [];
  if (spec.width_mm) specBits.push(`${spec.width_mm}mm`);
  if (spec.thickness_micron) specBits.push(`${spec.thickness_micron}µ`);
  if (spec.roll_length_m) specBits.push(`${spec.roll_length_m}m`);

  let tileW = preset.pageW;
  let tileH = preset.pageH;
  if (preset.key === "104x50") { tileW = 50; tileH = 50; }
  else if (preset.key === "a4grid") { tileW = 100; tileH = 40; }

  // Header/caption text always follows the global scale so the tiny "Carton
  // No." labels above each value stay proportional. Only the *value* text
  // reads from `fieldSizes`.
  const capPx = (base) => `${(base * fontScale).toFixed(2)}px`;
  const valPx = (key) => `${effectiveFieldSize(key, fieldSizes, fontScale).toFixed(2)}px`;
  // Weight unit "kg" should track the gross number.
  const unitPx = (key) => {
    const v = effectiveFieldSize(key, fieldSizes, fontScale);
    return `${(v * 0.48).toFixed(2)}px`;
  };

  return (
    <div
      data-label-card
      data-testid={`carton-label-${d?.box_no || "unknown"}`}
      className="bg-white border-2 border-zinc-950 text-zinc-950 print:border-2 print:shadow-none shadow-md flex flex-col items-center justify-between text-center"
      style={{ width: `${tileW}mm`, height: `${tileH}mm`, padding: "3mm", boxSizing: "border-box", fontFamily }}
    >
      {fieldSet.has("company") && (
        <div className="font-mono uppercase tracking-widest text-zinc-500 leading-none" style={{ fontSize: valPx("company") }}>
          {COMPANY_NAME}
        </div>
      )}
      {fieldSet.has("box_no") && (
        <div className="w-full">
          <div className="font-mono uppercase tracking-[0.2em] text-zinc-500 leading-none" style={{ fontSize: capPx(6) }}>Carton No.</div>
          <div className="font-mono font-extrabold tracking-tight leading-none mt-0.5" style={{ fontSize: valPx("box_no") }} data-testid="label-box-no">
            {d?.box_no || "—"}
          </div>
        </div>
      )}
      {fieldSet.has("gross") && (
        <div className="w-full">
          <div className="font-mono uppercase tracking-[0.25em] text-zinc-600 leading-none" style={{ fontSize: capPx(7) }}>Gross Weight</div>
          <div className="font-bold tracking-tight leading-none mt-0.5" style={{ fontSize: valPx("gross") }} data-testid="label-gross">
            {(d?.gross_weight_kg ?? 0).toFixed(3)}
            <span className="font-bold ml-0.5" style={{ fontSize: unitPx("gross") }}>kg</span>
          </div>
        </div>
      )}
      {fieldSet.has("net") && (
        <div className="w-full">
          <div className="font-mono uppercase tracking-[0.2em] text-zinc-500 leading-none" style={{ fontSize: capPx(6) }}>Net Weight</div>
          <div className="font-bold leading-none mt-0.5" style={{ fontSize: valPx("net") }}>
            {(d?.net_weight_kg ?? 0).toFixed(3)} kg
          </div>
        </div>
      )}
      {fieldSet.has("dc_net") && dcNetWeight && (
        <div className="w-full">
          <div className="font-mono uppercase tracking-[0.2em] text-zinc-500 leading-none" style={{ fontSize: capPx(6) }}>Net Wt (as per DC)</div>
          <div className="font-bold leading-none mt-0.5" style={{ fontSize: valPx("dc_net") }} data-testid="label-dc-net">
            {dcNetWeight} kg
          </div>
        </div>
      )}
      {fieldSet.has("sku") && (
        <div className="w-full">
          <div className="font-mono uppercase tracking-[0.2em] text-zinc-500 leading-none" style={{ fontSize: capPx(6) }}>SKU</div>
          <div className="font-bold leading-tight mt-0.5 break-words" style={{ fontSize: valPx("sku") }} data-testid="label-sku">
            {d?.sku || "—"}
          </div>
        </div>
      )}
      {fieldSet.has("spec") && specBits.length > 0 && (
        <div className="w-full font-mono text-zinc-700" style={{ fontSize: valPx("spec") }}>{specBits.join(" · ")}</div>
      )}
      {fieldSet.has("produced_at") && d?.produced_at && (
        <div className="w-full font-mono text-zinc-500" style={{ fontSize: valPx("produced_at") }}>
          Made: {String(d.produced_at).slice(0, 10)}
        </div>
      )}
      {fieldSet.has("custom") && customText && (
        <div className="w-full font-mono text-orange-700 italic" style={{ fontSize: valPx("custom") }}>{customText}</div>
      )}
    </div>
  );
}
